import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Copy, Plus, RefreshCw, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { adminApi } from "../../api/admin";
import { useAuth } from "../../auth/AuthContext";
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  IconButton,
  Input,
  Modal,
  PageHeader,
  SearchInput,
  Switch,
  formatDateTime,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";
import type { Tenant } from "../../types";
import { errorText } from "./labels";

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOMAIN = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i;

function AddOrganizationModal({ open, onClose, existing }: { open: boolean; onClose: () => void; existing: Tenant[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setDomain("");
      setTenantId("");
      setTouched(false);
      setError(null);
    }
  }, [open]);

  const id = tenantId.trim().toLowerCase();
  const dom = domain.trim().toLowerCase().replace(/^@/, "");
  const idError = !id ? null : !GUID.test(id) ? "That doesn't look like a tenant ID. It has 36 characters, like 8a709df5-f485-4717-8c31-7ea5e4cc1ff4." : existing.some((t) => t.tenant_id.toLowerCase() === id) ? "This organization is already on the list." : null;
  const domainError = dom && !DOMAIN.test(dom) ? "Enter a domain like maridive.com." : null;
  const valid = !!name.trim() && !!dom && !!id && !idError && !domainError;

  const create = useMutation({
    mutationFn: () => adminApi.createTenant({ tenant_id: id, display_name: name.trim(), primary_domain: dom }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
      void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
      toast.success("Organization added", `People from ${name.trim()} can now sign in. Give them a role in People & roles after their first sign-in.`);
      onClose();
    },
    onError: (e) => setError(errorText(e, "The organization couldn't be added.")),
  });

  const submit = () => {
    setTouched(true);
    if (valid && !create.isPending) create.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add an organization"
      description="Allow people from another Microsoft 365 organization to sign in. They still need a role before they can see any reports."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={Plus} loading={create.isPending} disabled={!valid} onClick={submit}>
            Add organization
          </Button>
        </>
      }
    >
      <div className="ds-stack">
        {error ? <Banner tone="danger" title="Couldn't add the organization">{error}</Banner> : null}
        <Field label="Organization name" hint="How it's shown in this app, e.g. Maridive Projects.">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="e.g. Maridive Projects" />
        </Field>
        <Field label="Main email domain" error={touched || dom.length > 3 ? domainError ?? undefined : undefined} hint="For reference only — sign-in is checked by tenant ID.">
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} onBlur={() => setTouched(true)} placeholder="e.g. maridiveprojects.com" maxLength={200} />
        </Field>
        <Field
          label="Microsoft 365 tenant ID"
          error={touched || id.length >= 36 ? idError ?? undefined : undefined}
          hint="Ask IT, or find it in the Microsoft Entra admin center under Overview → Tenant ID."
        >
          <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} onBlur={() => setTouched(true)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="ds-mono" spellCheck={false} autoComplete="off" />
        </Field>
      </div>
    </Modal>
  );
}

export function TenantsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { user: me } = useAuth();
  const tenants = useQuery({ queryKey: ["admin", "tenants"], queryFn: adminApi.listTenants });
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers });
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");

  const peopleCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const u of users.data ?? []) m.set(u.entra_tenant_id, (m.get(u.entra_tenant_id) ?? 0) + 1);
    return m;
  }, [users.data]);
  const myTenant = (users.data ?? []).find((u) => u.id === me?.id)?.entra_tenant_id;

  const all = tenants.data ?? [];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((t) => !q || [t.display_name, t.primary_domain, t.tenant_id].join(" ").toLowerCase().includes(q));
  }, [all, search]);
  const activeCount = all.filter((t) => t.is_active).length;

  const toggle = useMutation({
    mutationFn: ({ t, active }: { t: Tenant; active: boolean }) => adminApi.setTenantActive(t.id, active),
    onSuccess: (_r, { t, active }) => {
      void qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
      void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
      toast.success(active ? "Organization allowed" : "Organization blocked", active ? `People from ${t.display_name} can sign in again.` : `People from ${t.display_name} can no longer sign in.`);
    },
    onError: (e) => toast.error("That didn't work", errorText(e)),
  });

  const onToggle = async (t: Tenant, active: boolean) => {
    if (!active) {
      const n = peopleCount.get(t.tenant_id) ?? 0;
      const mine = t.tenant_id === myTenant;
      const ok = await confirm({
        title: `Block sign-in for ${t.display_name}?`,
        message: (
          <>
            {n ? `${n} ${n === 1 ? "person" : "people"} from this organization won't be able to sign in` : "Nobody from this organization will be able to sign in"} until you allow it again. Their accounts, roles and reports are kept.
            {mine ? (
              <div style={{ marginTop: 12 }}>
                <Banner tone="warning" title="This is your own organization">
                  You'll be blocked too the next time you sign in.
                </Banner>
              </div>
            ) : null}
          </>
        ),
        confirmLabel: "Block sign-in",
        tone: "danger",
      });
      if (!ok) return;
    }
    toggle.mutate({ t, active });
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Tenant ID copied");
    } catch {
      toast.error("Couldn't copy", "Select the ID and copy it manually.");
    }
  };

  return (
    <div className="ds-page">
      <PageHeader
        title="Organizations"
        subtitle="Microsoft 365 organizations whose people are allowed to sign in. Anyone from an organization that isn't on this list is turned away."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>
            Add organization
          </Button>
        }
      />

      {tenants.isError ? (
        <Banner tone="danger" title="Organizations couldn't be loaded" action={<Button size="sm" icon={RefreshCw} onClick={() => void tenants.refetch()}>Try again</Button>}>
          {errorText(tenants.error)}
        </Banner>
      ) : (
        <>
          {tenants.isSuccess && all.length > 0 && activeCount === 0 ? (
            <div style={{ marginBottom: 16 }}>
              <Banner tone="warning" title="Every organization is blocked">
                Nobody can sign in right now. Allow at least one organization.
              </Banner>
            </div>
          ) : null}
          {all.length > 5 ? (
            <div className="ds-toolbar">
              <SearchInput value={search} onChange={setSearch} placeholder="Search by name, domain or tenant ID" width={320} />
            </div>
          ) : null}
          <Card flush>
            <DataTable<Tenant>
              rows={tenants.data ? rows : undefined}
              loading={tenants.isLoading}
              rowKey={(t) => t.id}
              initialSort={{ key: "name", dir: "asc" }}
              empty={
                all.length === 0 ? (
                  <EmptyState
                    icon={Building2}
                    title="No organizations allowed yet"
                    description="Add your company's Microsoft 365 organization so people can sign in."
                    action={<Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>Add organization</Button>}
                  />
                ) : (
                  <EmptyState icon={Search} title="No organizations match" description="Try another search." action={<Button onClick={() => setSearch("")}>Clear search</Button>} />
                )
              }
              columns={[
                {
                  key: "name",
                  header: "Organization",
                  width: "30%",
                  sortValue: (t) => t.display_name,
                  cell: (t) => (
                    <div className="ds-row" style={{ gap: 10, minWidth: 0 }}>
                      <span className="ds-stat__icon" style={{ width: 34, height: 34, borderRadius: 9 }}>
                        <Building2 size={16} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="ds-row" style={{ gap: 6 }}>
                          <span className="ds-truncate" style={{ fontWeight: 600 }}>{t.display_name}</span>
                          {t.tenant_id === myTenant ? <Badge>Yours</Badge> : null}
                        </div>
                        <div className="ds-muted ds-small">{t.primary_domain}</div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "people",
                  header: "People",
                  align: "right",
                  sortValue: (t) => peopleCount.get(t.tenant_id) ?? 0,
                  cell: (t) => (users.data ? peopleCount.get(t.tenant_id) ?? 0 : <span className="ds-muted">—</span>),
                },
                {
                  key: "tenant",
                  header: "Tenant ID",
                  sortValue: (t) => t.tenant_id,
                  cell: (t) => (
                    <div className="ds-row" style={{ gap: 2 }}>
                      <span className="ds-mono ds-muted" title={t.tenant_id}>
                        {t.tenant_id}
                      </span>
                      <IconButton icon={Copy} label="Copy tenant ID" size="sm" onClick={() => void copy(t.tenant_id)} />
                    </div>
                  ),
                },
                {
                  key: "added",
                  header: "Added",
                  sortValue: (t) => t.created_at,
                  cell: (t) => (
                    <span className="ds-muted" title={formatDateTime(t.created_at)}>
                      {formatRelative(t.created_at)}
                    </span>
                  ),
                },
                {
                  key: "status",
                  header: "Can sign in",
                  sortValue: (t) => (t.is_active ? 0 : 1),
                  cell: (t) => (
                    <Switch
                      checked={t.is_active}
                      disabled={toggle.isPending}
                      onChange={(v) => void onToggle(t, v)}
                      label={t.is_active ? <Badge tone="success" dot>Allowed</Badge> : <Badge tone="danger" dot>Blocked</Badge>}
                    />
                  ),
                },
              ]}
            />
          </Card>
          {all.length ? (
            <p className="ds-small ds-faint" style={{ marginTop: 12 }}>
              Allowing an organization only lets its people sign in. What they can see is set by their role in People &amp; roles.
            </p>
          ) : null}
        </>
      )}

      <AddOrganizationModal open={adding} onClose={() => setAdding(false)} existing={all} />
    </div>
  );
}
